import React, { useEffect, useMemo } from 'react';
import type { TFunction } from 'i18next';
import type { VisitorPassQueryResponse } from 'application-types';
import { convertToUUID, compareUUIDs } from 'uuid-encoding';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import type { GetVisitorCustomFieldConfigsQuery } from 'graphql-query-contracts';
import { isVisitorCustomFieldSelectType } from 'constants-visitor-management';
import { TruncateTextComponent } from '../components/TruncateText/TruncateText';
import { VISITOR_CUSTOM_FIELD_MAX_DISPLAY_LENGTH } from '../constants';
import { useVisitorCustomFieldConfigs } from './useVisitorCustomFieldConfigs';

type ColumnVisibilityState = Record<string, boolean>;

type VisitorCustomFieldValue = {
  configId: string;
  value: string[];
};

type CustomFieldConfig =
  GetVisitorCustomFieldConfigsQuery['visitorCustomFieldConfigs'][number];

function resolveDisplayValues(
  values: string[] | undefined,
  config: CustomFieldConfig
): string[] {
  if (!values?.length) return [];

  if (!isVisitorCustomFieldSelectType(config.fieldType) || !config.options) {
    return values;
  }

  return values.map(
    v => config.options?.find(opt => opt.value === v)?.displayText ?? v
  );
}

type VisitorPassWithCustomFields = VisitorPassQueryResponse & {
  customFieldValues?: VisitorCustomFieldValue[];
};

export function useVisitorCustomFieldColumns({
  channelId,
  visibleColumns,
  t,
}: {
  channelId: string;
  visibleColumns: ColumnVisibilityState | undefined;
  t: TFunction;
}) {
  const customFieldsEnabled = useFlag(
    FeatureFlag.VisitorManagementCustomFields,
    false
  );
  const {
    data: visitorCustomFieldConfigs,
    fetch: fetchVisitorCustomFieldConfigs,
    error: customFieldConfigError,
  } = useVisitorCustomFieldConfigs();

  useEffect(() => {
    if (!customFieldsEnabled || !channelId) return;

    fetchVisitorCustomFieldConfigs(convertToUUID(channelId));
  }, [channelId, customFieldsEnabled, fetchVisitorCustomFieldConfigs]);

  const customFieldColumns = useMemo(
    () =>
      (customFieldsEnabled
        ? visitorCustomFieldConfigs.filter(customField => customField.isEnabled)
        : []
      ).map(customFieldConfig => ({
        header: (
          <TruncateTextComponent
            text={customFieldConfig.fieldName}
            maxLength={VISITOR_CUSTOM_FIELD_MAX_DISPLAY_LENGTH}
          />
        ),
        csvHeader: customFieldConfig.fieldName,
        key: `customField.${customFieldConfig.id}`,
        customFieldSection: t('web.table.editColumns.section.customFields'),
        renderCell: (_: unknown, row: VisitorPassQueryResponse) => {
          const values = (row as VisitorPassWithCustomFields)
            .customFieldValues?.find(
              v => compareUUIDs(v.configId, customFieldConfig.id)
            )?.value;
          const displayValues = resolveDisplayValues(values, customFieldConfig);
          const displayText = displayValues.join(', ') || '--';

          return (
            <TruncateTextComponent
              text={displayText}
              maxLength={VISITOR_CUSTOM_FIELD_MAX_DISPLAY_LENGTH}
            />
          );
        },
        renderForCSV: (_: unknown, row: VisitorPassQueryResponse) => {
          const values = (row as VisitorPassWithCustomFields)
            .customFieldValues?.find(
              v => compareUUIDs(v.configId, customFieldConfig.id)
            )?.value;

          return resolveDisplayValues(values, customFieldConfig).join(', ');
        },
      })),
    [customFieldsEnabled, visitorCustomFieldConfigs, t]
  );

  const effectiveColumnVisibility = useMemo(() => {
    const base = visibleColumns ?? {};
    const hidden: ColumnVisibilityState = {};

    for (const column of customFieldColumns) {
      if (!(column.key in base)) {
        hidden[column.key] = false;
      }
    }

    return Object.keys(hidden).length > 0 ? { ...base, ...hidden } : visibleColumns;
  }, [visibleColumns, customFieldColumns]);

  return {
    customFieldColumns,
    effectiveColumnVisibility,
    customFieldsEnabled,
    isCustomFieldConfigError: !!customFieldConfigError,
  };
}
